import {
  Bot,
  MessageCircle,
  DatabaseZap,
  Workflow,
  Wrench,
  Shield,
  Code,
  Users,
  Briefcase,
  CircleDollarSign,
  PhoneCall,
  Search,
} from "lucide-react";
import { ComponentType } from "react";
import {
  VoiceAgentAnimation,
  ChatbotAnimation,
  RAGAnimation,
  WorkflowAnimation,
  CustomSolutionAnimation,
  SecurityAnimation,
} from "@/components/ui/lottie-animation";

export type Service = {
  slug: string;
  title: string;
  animationComponent?: ComponentType;
  description: string;
  icon: any;
  imageUrl: string;
  content: {
    hero: string;
    benefits: string[];
    useCase: string;
    cta: string;
    keywords: string;
  };
};
export type OtherService = {
  title: string;
  description: string;
  icon: any;
};
export const services: Service[] = [
  {
    slug: "ai-voice-agents",
    title: "AI Voice Agents",
    animationComponent: VoiceAgentAnimation,
    description:
      "Automate customer support and scheduling with AI that can handle inquiries 24/7.",
    icon: Bot,
    imageUrl: "/voiceAgent.jpg",
    content: {
      hero: "Engage customers with intelligent, human-like voice conversations. Our AI Voice Agents can handle inbound and outbound calls, book appointments, and answer common questions, freeing up your staff to focus on higher-value tasks.",
      benefits: [
        "Reduce call center costs by up to 60%",
        "24/7 availability ensures no missed customer calls",
        "Seamless integration with your existing CRM and scheduling software like Calendly and ServiceTitan",
        "Improve customer satisfaction with instant responses",
      ],
      useCase:
        "A bustling dental practice reduced appointment no-shows by 20% and saved 15 hours of administrative work per week by using our AI voice agent for automated reminders and rescheduling.",
      cta: "See Voice Agents in Action",
      keywords:
        "AI voice agents for small businesses, voice automation for dentists, automated appointment reminders",
    },
  },
  {
    slug: "ai-chatbots",
    title: "AI Chatbots",
    animationComponent: ChatbotAnimation,
    description:
      "Deploy intelligent chatbots on your website and social media for instant lead capture and support.",
    icon: MessageCircle,
    imageUrl: "/K-12 & Higher Ed.jpg",
    content: {
      hero: "Turn your website into a lead generation machine. Our AI chatbots engage visitors in real-time, answer FAQs, qualify leads, and book appointments directly within the chat.",
      benefits: [
        "Increase lead conversion rates by up to 30%",
        "Provide 24/7 customer support on your website and social media",
        "No-code deployment for quick and easy setup",
        "Capture valuable customer data for marketing campaigns",
      ],
      useCase:
        "A local pet grooming salon boosted online bookings by 40% and captured 50 new leads per month by integrating our AI chatbot on their website and Facebook page.",
      cta: "Try Our Chatbot Demo",
      keywords:
        "AI chatbots for SMMBs, website chatbot solutions, lead generation chatbot",
    },
  },
  {
    slug: "rag-systems",
    title: "RAG Systems",
    animationComponent: RAGAnimation,
    description:
      "Power your AI with real-time, accurate information from your own data sources.",
    icon: DatabaseZap,
    imageUrl: "/RAGSystems.jpg",
    content: {
      hero: "Eliminate \"I don't know\" from your AI's vocabulary. Retrieval-Augmented Generation (RAG) systems allow your chatbot or voice agent to access your latest documents, product catalogs, or internal knowledge bases to provide accurate, up-to-date answers.",
      benefits: [
        "Ensure highly accurate and context-aware responses",
        "Reduce hallucinations and incorrect information",
        "Easily scalable as your knowledge base grows",
        "Securely connects to your private data sources",
      ],
      useCase:
        "A real estate agency uses a RAG system to provide instant, detailed information on property listings, including pricing, availability, and features, leading to more informed and qualified buyers.",
      cta: "Learn More About RAG",
      keywords:
        "RAG chatbots for businesses, AI data retrieval solutions, custom knowledge base AI",
    },
  },
  {
    slug: "workflow-automation",
    title: "Workflow Automation",
    animationComponent: WorkflowAnimation,
    description:
      "Automate repetitive tasks like invoicing, email campaigns, and data entry.",
    icon: Workflow,
    imageUrl: "/AutomatioWorkflow.jpg",
    content: {
      hero: "Stop wasting time on manual processes. We analyze your daily operations and build custom AI-powered automations to handle everything from invoicing and scheduling to email marketing campaigns and social media posting.",
      benefits: [
        "Save over 20 hours of manual work per week",
        "Reduce human error and improve data accuracy",
        "Seamless integrations with tools you already use like Zapier, HubSpot, and QuickBooks",
        "Streamline your entire business process from lead to payment",
      ],
      useCase:
        "A chiropractic clinic automated its patient reminder and follow-up system, reducing administrative overhead and improving patient retention by 25%.",
      cta: "Automate Your Workflow Today",
      keywords:
        "AI workflow automation, business process automation, small business automation",
    },
  },
  {
    slug: "custom-ai-solutions",
    title: "Custom AI Solutions",
    animationComponent: CustomSolutionAnimation,
    description:
      "Get tailor-made AI agents and strategic consultation to solve your unique business challenges.",
    icon: Wrench,
    imageUrl: "/CustomConsultation.jpg",
    content: {
      hero: "Have a unique challenge? We provide bespoke AI development and strategic consultation to create solutions perfectly aligned with your business goals. If you can imagine it, we can build it.",
      benefits: [
        "Solutions built specifically for your business needs",
        "Gain a competitive edge with proprietary AI technology",
        "Expert guidance and ongoing support from our team of AI specialists",
        "From idea to implementation, we are your strategic AI partner",
      ],
      useCase:
        "A boutique fitness studio partnered with us to build a custom AI booking agent that manages complex class schedules and personalized member recommendations, resulting in a 35% increase in class attendance.",
      cta: "Request a Consultation",
      keywords:
        "custom AI agents, AI consultation for small businesses, bespoke AI solutions",
    },
  },
  {
    slug: "ai-security-fraud-detection",
    title: "AI Security & Fraud Detection",
    animationComponent: SecurityAnimation,
    description:
      "Protect your business with intelligent AI systems that detect and prevent security threats and fraudulent activities in real-time.",
    icon: Shield,
    imageUrl: "/AISecurity.jpg",
    content: {
      hero: "Stay ahead of cyber threats and fraudulent behavior with AI-driven security solutions. Our systems continuously monitor your operations, transactions, and communications to detect anomalies and prevent risks before they escalate.",
      benefits: [
        "Real-time fraud detection and prevention",
        "AI-powered monitoring of transactions, communications, and user behavior",
        "Customizable alerts and automated incident response",
        "Safeguard your assets and reputation with intelligent, proactive security",
      ],
      useCase:
        "An e-commerce platform integrated our AI fraud detection system to monitor thousands of daily transactions, reducing fraudulent activities by 60% within the first quarter.",
      cta: "Schedule a Security Audit",
      keywords:
        "AI security solutions, fraud detection AI, real-time fraud prevention, AI cyber threat monitoring",
    },
  },
];
export const otherservices: OtherService[] = [
  {
    title: "Web Development",
    description:
      "We build custom web applications to help you achieve your business goals.",
    icon: Code,
  },
  {
    title: "Business Outsourcing",
    description:
      "We help you outsource your business processes to a team of experts.",
    icon: Briefcase,
  },
  {
    title: "Back Office BPO Services",
    description:
      "We help you outsource your back office processes to a team of experts.",
    icon: PhoneCall,
  },
  {
    title: "Digital Marketing",
    description:
      "We help you market your business online to reach your target audience.",
    icon: CircleDollarSign,
  },
  {
    title: "Virtual Assistant Services",
    description:
      "We help you manage your business operations with a team of virtual assistants.",
    icon: Users,
  },
  {
    title: "Search Engine Optimization",
    description:
      "We help you optimize your website for search engines to improve your online visibility.",
    icon: Search,
  },
];
